import type { ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Icon, type IconName } from "./Icon";
import { useSnapshot } from "@/lib/snapshot-context";
import { getAuth, isAdmin, signOut } from "@/lib/auth";

interface NavItem {
  id: string;
  label: string;
  icon: IconName | string;
  to: string;
  count?: number;
  adminOnly?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

function useNavGroups(): NavGroup[] {
  const { data } = useSnapshot();
  const activeIncidents = data.incidents.filter(
    (i) => i.status !== "resolved",
  ).length;
  return [
    {
      label: "Monitor",
      items: [
        { id: "overview", label: "Overview", icon: "dashboard", to: "/" },
        {
          id: "incidents",
          label: "Incidents",
          icon: "alert",
          to: "/incidents",
          count: activeIncidents,
        },
      ],
    },
    {
      label: "Admin",
      items: [
        {
          id: "services",
          label: "Services",
          icon: "list",
          to: "/admin/services",
          count: data.services.length,
          adminOnly: true,
        },
        {
          id: "add",
          label: "Add service",
          icon: "plus",
          to: "/admin/add",
          adminOnly: true,
        },
        {
          id: "webhooks",
          label: "Webhook inspector",
          icon: "webhook",
          to: "/admin/webhooks",
          adminOnly: true,
        },
      ],
    },
    {
      label: "Workspace",
      items: [
        { id: "settings", label: "Settings", icon: "settings", to: "/settings" },
      ],
    },
  ];
}

export function Layout({ children }: { children: ReactNode }) {
  const groups = useNavGroups();
  const admin = isAdmin();
  const auth = getAuth();
  const initials = (auth?.email ?? "??")
    .split(/[@.]/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "232px 1fr",
        minHeight: "100vh",
      }}
    >
      <aside
        style={{
          background: "var(--sidebar)",
          color: "var(--sidebar-foreground)",
          borderRight: "1px solid var(--sidebar-border)",
          display: "flex",
          flexDirection: "column",
          padding: "14px 10px",
          position: "sticky",
          top: 0,
          height: "100vh",
        }}
      >
        <BrandMark />

        <div style={{ position: "relative", marginBottom: 14 }}>
          <Icon
            name="search"
            size={13}
            style={{
              position: "absolute",
              left: 9,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--muted-foreground)",
            }}
          />
          <input
            placeholder="Search services…"
            style={{
              width: "100%",
              height: 30,
              padding: "0 36px 0 28px",
              borderRadius: 8,
              border: "1px solid var(--input)",
              background: "var(--background)",
              font: "inherit",
              fontSize: 12,
              outline: "none",
              color: "inherit",
            }}
          />
          <span
            style={{
              position: "absolute",
              right: 7,
              top: "50%",
              transform: "translateY(-50%)",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              background: "var(--muted)",
              padding: "1px 5px",
              borderRadius: 4,
              color: "var(--muted-foreground)",
            }}
          >
            ⌘K
          </span>
        </div>

        {groups
          .filter((g) => admin || g.label === "Monitor")
          .map((g) => {
          const items = g.items.filter((i) => !i.adminOnly || admin);
          if (items.length === 0) return null;
          return (
            <div key={g.label} style={{ marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "var(--muted-foreground)",
                  fontWeight: 600,
                  padding: "6px 10px",
                }}
              >
                {g.label}
              </div>
              <div>
                {items.map((item) => (
                  <SideLink key={item.id} item={item} />
                ))}
              </div>
            </div>
          );
        })}

        {auth ? (
          <div
            style={{
              marginTop: "auto",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: 8,
              borderRadius: 8,
              background: "var(--sidebar-accent)",
            }}
          >
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "var(--primary)",
                color: "var(--primary-foreground)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: 11,
              }}
            >
              {initials || "??"}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {auth?.email ?? "—"}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--muted-foreground)",
                  lineHeight: 1.2,
                }}
              >
                {admin ? "admin" : "signed in"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                signOut();
                window.location.assign("/");
              }}
              title="Sign out"
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                border: "none",
                background: "transparent",
                color: "var(--muted-foreground)",
                cursor: "pointer",
              }}
            >
              <Icon name="logout" size={14} />
            </button>
          </div>
        ) : (
          <a
            href="/signin"
            style={{
              marginTop: "auto",
              padding: "10px 12px",
              borderRadius: 8,
              background: "var(--sidebar-accent)",
              color: "inherit",
              textDecoration: "none",
              fontSize: 12,
              textAlign: "center",
            }}
          >
            Admin sign-in
          </a>
        )}
      </aside>

      <main
        style={{ display: "flex", flexDirection: "column", minWidth: 0 }}
      >
        {children}
      </main>
    </div>
  );
}

function BrandMark() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "6px 8px",
        marginBottom: 16,
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: 7,
          background: "var(--primary)",
          color: "var(--primary-foreground)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Icon name="pulse" size={15} />
        <span
          style={{
            position: "absolute",
            top: -2,
            right: -2,
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "var(--destructive)",
            boxShadow:
              "0 0 0 2px var(--sidebar), 0 0 0 4px color-mix(in oklch, var(--destructive) 30%, transparent)",
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          lineHeight: 1.1,
          minWidth: 0,
        }}
      >
        <span
          style={{ fontSize: 14, fontWeight: 700, letterSpacing: "-0.01em" }}
        >
          Status Watch
        </span>
      </div>
    </div>
  );
}

function SideLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      style={({ isActive }) => ({
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 10px",
        borderRadius: 8,
        color: "inherit",
        textDecoration: "none",
        fontSize: 13,
        fontWeight: 500,
        position: "relative",
        background: isActive ? "var(--sidebar-accent)" : "transparent",
        cursor: "pointer",
      })}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              style={{
                position: "absolute",
                left: 0,
                width: 3,
                height: 14,
                background: "var(--sidebar-primary)",
                borderRadius: 2,
              }}
            />
          )}
          <span
            style={{
              color: isActive
                ? "var(--sidebar-primary)"
                : "var(--muted-foreground)",
            }}
          >
            <Icon name={item.icon} size={15} />
          </span>
          {item.label}
          {item.count != null && (
            <span
              style={{
                marginLeft: "auto",
                fontSize: 10,
                background: "var(--muted)",
                padding: "1px 6px",
                borderRadius: 999,
                color: "var(--muted-foreground)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {item.count}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

interface PageHeaderProps {
  crumbs?: string[];
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}

export function PageHeader({
  crumbs = [],
  title,
  subtitle,
  actions,
}: PageHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <>
      <header
        style={{
          height: 52,
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          padding: "0 24px",
          gap: 14,
          background: "var(--background)",
          position: "sticky",
          top: 0,
          zIndex: 5,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            color: "var(--muted-foreground)",
          }}
        >
          {crumbs.map((c, i) => (
            <span key={`${c}-${i}`} style={{ display: "inline-flex", gap: 6 }}>
              {i > 0 && <span style={{ color: "var(--border)" }}>/</span>}
              <span
                style={{
                  color:
                    i === crumbs.length - 1
                      ? "var(--foreground)"
                      : "inherit",
                  fontWeight: i === crumbs.length - 1 ? 500 : 400,
                }}
              >
                {c}
              </span>
            </span>
          ))}
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <button
            type="button"
            title="Refresh"
            onClick={() => navigate(location.pathname + location.search, { replace: true })}
            style={iconBtn}
          >
            <Icon name="refresh" size={15} />
          </button>
          <button type="button" title="Notifications" style={iconBtn}>
            <Icon name="bell" size={15} />
          </button>
        </div>
      </header>
      {(title || actions) && (
        <div
          style={{
            padding: "20px 32px 0",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            {title && (
              <h1
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                }}
              >
                {title}
              </h1>
            )}
            {subtitle && (
              <div
                style={{
                  color: "var(--muted-foreground)",
                  fontSize: 13,
                  marginTop: 4,
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          {actions && (
            <div style={{ display: "flex", gap: 8 }}>{actions}</div>
          )}
        </div>
      )}
    </>
  );
}

const iconBtn = {
  width: 30,
  height: 30,
  borderRadius: 8,
  border: "1px solid transparent",
  background: "transparent",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color: "var(--muted-foreground)",
};
